import { ArrowRight, Briefcase, Loader2, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';

type Intent = 'audit' | 'operations' | 'investor' | 'partnership' | 'press' | 'other';

const INTENT_OPTIONS: { value: Intent; label: string }[] = [
  { value: 'audit', label: 'Brand audit (sellers)' },
  { value: 'operations', label: 'Managed POD operations' },
  { value: 'investor', label: 'Investor inquiry' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'press', label: 'Press' },
  { value: 'other', label: 'Other' },
];

const initialForm = {
  name: '',
  email: '',
  companyName: '',
  brandUrl: '',
  intent: 'audit' as Intent,
  description: '',
  marketingConsent: false,
};

type FormState = typeof initialForm;

function validateForm(form: FormState): string | null {
  if (!form.name.trim()) return 'Please enter your name';
  if (!form.email.trim()) return 'Please enter your email';
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(form.email.trim())) return 'Invalid email address';
  if (!form.intent) return 'Please pick an intent';
  if (form.description.trim().length < 30) {
    return 'Please describe your brand or inquiry (at least 30 characters)';
  }
  return null;
}

function readIntentFromQuery(): Intent | null {
  if (typeof window === 'undefined') return null;
  const v = new URLSearchParams(window.location.search).get('intent');
  return INTENT_OPTIONS.some((opt) => opt.value === v) ? (v as Intent) : null;
}

const CTA = () => {
  const [form, setForm] = useState<FormState>(initialForm);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    const queryIntent = readIntentFromQuery();
    if (queryIntent) {
      setForm((prev) => ({ ...prev, intent: queryIntent }));
    }
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const target = e.target;
    const name = target.name as keyof FormState;
    const value =
      target instanceof HTMLInputElement && target.type === 'checkbox'
        ? target.checked
        : target.value;
    setForm((prev) => ({ ...prev, [name]: value }));
    setMessage(null);
  };

  const pickIntent = (intent: Intent) => {
    setForm((prev) => ({ ...prev, intent }));
    setMessage(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const err = validateForm(form);
    if (err) {
      setMessage({ type: 'error', text: err });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyName: form.companyName.trim() || form.name.trim(),
          email: form.email.trim(),
          name: form.name.trim(),
          intent: form.intent,
          brandUrl: form.brandUrl.trim() || undefined,
          marketingConsent: form.marketingConsent,
          description: form.description.trim(),
        }),
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          json?.error?.message ?? json?.message ?? 'Submission failed. Please try again.'
        );
      }

      setMessage({
        type: 'success',
        text: 'Thanks — we received your message. Expect a reply within 2 business days.',
      });
      setForm({ ...initialForm, intent: form.intent });
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Something went wrong. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-20 px-6 bg-black" id="cta">
      <div className="max-w-7xl mx-auto space-y-12">
        {/* Dual-track cards */}
        <div className="grid md:grid-cols-2 gap-6">
          <button
            type="button"
            onClick={() => pickIntent('audit')}
            className={`group text-left p-8 rounded-2xl border transition-all duration-300 ${
              form.intent === 'audit'
                ? 'bg-gradient-to-br from-purple-900/40 to-pink-900/30 border-purple-500/60'
                : 'bg-gray-900/40 border-gray-800 hover:border-purple-500/40'
            }`}
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <Search size={20} className="text-purple-300" />
              </div>
              <span className="text-purple-300 text-xs font-medium tracking-widest uppercase">
                For sellers
              </span>
            </div>
            <h3 className="text-2xl md:text-3xl font-bold mb-2 text-white">
              Already have a POD brand?
            </h3>
            <p className="text-gray-300 mb-4">
              Have AITOS operate it. Start with a 2-week brand audit and an action plan.
            </p>
            <span className="inline-flex items-center gap-2 text-white font-medium group-hover:gap-3 transition-all">
              Book a brand audit
              <ArrowRight size={18} />
            </span>
          </button>

          <button
            type="button"
            onClick={() => pickIntent('investor')}
            className={`group text-left p-8 rounded-2xl border transition-all duration-300 ${
              form.intent === 'investor'
                ? 'bg-gradient-to-br from-purple-900/40 to-pink-900/30 border-purple-500/60'
                : 'bg-gray-900/40 border-gray-800 hover:border-purple-500/40'
            }`}
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-pink-500/20 flex items-center justify-center">
                <Briefcase size={20} className="text-pink-300" />
              </div>
              <span className="text-pink-300 text-xs font-medium tracking-widest uppercase">
                For investors / partners
              </span>
            </div>
            <h3 className="text-2xl md:text-3xl font-bold mb-2 text-white">
              Evaluating the AITOS operating model?
            </h3>
            <p className="text-gray-300 mb-4">
              Talk to the founders. We share the operating model, unit economics, and roadmap on
              request.
            </p>
            <span className="inline-flex items-center gap-2 text-white font-medium group-hover:gap-3 transition-all">
              Talk to the founders
              <ArrowRight size={18} />
            </span>
          </button>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left: heading + contact info */}
          <div className="space-y-6">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight text-white">
              Tell us about your brand —
              <span className="block bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">
                or your thesis.
              </span>
            </h2>
            <p className="text-base md:text-lg text-gray-400 font-light">
              One form, all intents. We reply within 2 business days.
            </p>

            <div className="space-y-4 text-gray-300">
              <p>
                <span className="text-gray-500 text-sm tracking-widest uppercase block mb-1">
                  Email
                </span>
                <a
                  href="mailto:hi@aitos.app"
                  className="text-lg hover:text-purple-400 transition-colors"
                >
                  hi@aitos.app
                </a>
              </p>
              <p>
                <span className="text-gray-500 text-sm tracking-widest uppercase block mb-1">
                  Phone
                </span>
                <a
                  href="tel:+13074301688"
                  className="text-lg hover:text-purple-400 transition-colors"
                >
                  +1 (307) 430-1688
                </a>
              </p>
              <address className="not-italic text-sm leading-relaxed">
                <span className="text-gray-500 text-sm tracking-widest uppercase block mb-1">
                  Mailing address
                </span>
                AITOS Labs LLC
                <br />
                30 N Gould St Ste N
                <br />
                Sheridan, WY 82801, United States
              </address>
              <p className="text-sm">
                <span className="text-gray-500 text-sm tracking-widest uppercase block mb-1">
                  Hours
                </span>
                Mon–Fri, 9 AM–6 PM PT · Reply within 2 business days
              </p>
            </div>
          </div>

          {/* Right: form */}
          <div className="bg-gray-900/40 rounded-3xl p-8 md:p-10 border border-gray-800 shadow-2xl backdrop-blur-sm relative overflow-hidden group">
            <div className="absolute -top-24 -right-24 w-48 h-48 bg-purple-500/10 rounded-full blur-3xl group-hover:bg-purple-500/20 transition-all duration-700" />

            <form onSubmit={handleSubmit} className="space-y-6 relative">
              {message && (
                <div
                  className={`p-4 rounded-lg text-sm ${
                    message.type === 'success'
                      ? 'bg-green-500/10 border border-green-500/30 text-green-400'
                      : 'bg-red-500/10 border border-red-500/30 text-red-400'
                  }`}
                >
                  {message.text}
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label
                    htmlFor="name"
                    className="block text-xs font-semibold text-gray-400 uppercase tracking-widest pl-1"
                  >
                    Name *
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    value={form.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-gray-900/60 border border-gray-800 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all"
                    placeholder="Your full name"
                  />
                </div>
                <div className="space-y-2">
                  <label
                    htmlFor="email"
                    className="block text-xs font-semibold text-gray-400 uppercase tracking-widest pl-1"
                  >
                    Work email *
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-gray-900/60 border border-gray-800 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all"
                    placeholder="you@company.com"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label
                    htmlFor="companyName"
                    className="block text-xs font-semibold text-gray-400 uppercase tracking-widest pl-1"
                  >
                    Company / brand
                  </label>
                  <input
                    id="companyName"
                    name="companyName"
                    type="text"
                    value={form.companyName}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-900/60 border border-gray-800 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all"
                    placeholder="Optional"
                  />
                </div>
                <div className="space-y-2">
                  <label
                    htmlFor="brandUrl"
                    className="block text-xs font-semibold text-gray-400 uppercase tracking-widest pl-1"
                  >
                    Brand URL
                  </label>
                  <input
                    id="brandUrl"
                    name="brandUrl"
                    type="text"
                    value={form.brandUrl}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-900/60 border border-gray-800 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all"
                    placeholder="https://"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="intent"
                  className="block text-xs font-semibold text-gray-400 uppercase tracking-widest pl-1"
                >
                  Intent *
                </label>
                <select
                  id="intent"
                  name="intent"
                  value={form.intent}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-900/60 border border-gray-800 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all"
                >
                  {INTENT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="description"
                  className="block text-xs font-semibold text-gray-400 uppercase tracking-widest pl-1"
                >
                  Message *
                </label>
                <textarea
                  id="description"
                  name="description"
                  rows={4}
                  value={form.description}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-900/60 border border-gray-800 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all resize-none"
                  placeholder="Tell us about your brand, the operating problem you want help with, or what you're evaluating (min 30 characters)."
                />
              </div>

              <label className="flex items-start gap-3 text-sm text-gray-400 cursor-pointer">
                <input
                  type="checkbox"
                  name="marketingConsent"
                  checked={form.marketingConsent}
                  onChange={handleChange}
                  className="mt-1 size-4 rounded border-gray-700 bg-gray-900 accent-purple-500"
                />
                <span>
                  I agree to receive occasional updates from AITOS Labs LLC. We comply with GDPR and
                  CCPA; you can unsubscribe at any time.
                </span>
              </label>

              <div className="pt-2">
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full justify-center py-6 text-lg font-bold bg-purple-600 hover:bg-purple-700 text-white rounded-xl shadow-lg shadow-purple-500/20 active:scale-[0.98] transition-all"
                >
                  {loading ? (
                    <>
                      <Loader2 className="size-5 animate-spin mr-2" />
                      Sending…
                    </>
                  ) : (
                    'Send'
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTA;
